import React, { useEffect, useState } from "react";
import { View, ScrollView } from "react-native";
import {
    BettingEventStatus,
    FreeplayEvent,
    FreeplayLeague,
    HorseRaceEvent,
    HorseRacingPrompt,
    PlatformEvent,
    PlatformOffer,
    SubmitHorseRacePicks,
    SubmitPicks,
} from "@freeplay/shared-code";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { GameActions, GameState } from "../../controllers/GameController";
import { ButtonSize, ButtonTheme, RoundedButton } from "../../components/buttons/roundedButton/RoundedButton";
import { GlobalStyles } from "../../components/style/Style";
import { useServices } from "../../contexts/ServiceContext";
import { GameTitle } from "../../components/picks/GameTitle";
import { MediaQueries } from "../../components/hooks/dynamicStyleHook";
import { useAgreements } from "../../components/hooks/agreementsHook";
import { format, parseISO } from "date-fns";
import { useEvent } from "../../contexts/EventContext";
import DefaultImage from "../../assets/default_background.png";
import DefaultTSImage from "../../assets/default_background_ts.png";
import { GameGroup, GameType } from "../../components/picks/GameGroup";
import { FavouritesButton } from "./FavouritesButton";
import { RandomButton } from "./RandomButton";
import { CountDownTimer } from "../../components/textcontainers/CountDownTimer";
import mixpanel from "mixpanel-browser";
import { MixPanelEventEnum } from "../../utils/analytics";
export interface GameViewProps {
    testID?: string;
    state: GameState;
    dispatch: React.Dispatch<GameActions>;
    sendPicks: () => void;
}

// TODO -  can we just combine w/ bespoke game view - lots of shared things?
// in effort for expediency, maybe this ain't so bad.
export const groupGames = (
    gameEntries: [PlatformEvent, PlatformOffer][],
): Map<number, [PlatformEvent, PlatformOffer][]> => {
    const groupedGames: Map<number, [PlatformEvent, PlatformOffer][]> = new Map<
        number,
        [PlatformEvent, PlatformOffer][]
    >();

    for (const gameEntry of gameEntries) {
        let group = groupedGames.get(gameEntry[0].id);

        if (!group) {
            group = [];
        }

        group.push(gameEntry);
        groupedGames.set(gameEntry[0].id, group);
    }

    return groupedGames;
};
export const groupHorseRaceGames = (
    horseGameEntries: [HorseRaceEvent, HorseRacingPrompt][],
): Map<number, [HorseRaceEvent, HorseRacingPrompt][]> => {
    const groupedGames: Map<number, [HorseRaceEvent, HorseRacingPrompt][]> = new Map<
        number,
        [HorseRaceEvent, HorseRacingPrompt][]
    >();

    for (const gameEntry of horseGameEntries) {
        let group = groupedGames.get(gameEntry[0].id);

        if (!group) {
            group = [];
        }

        group.push(gameEntry);
        groupedGames.set(gameEntry[0].id, group);
    }
    return groupedGames;
};

export const GameView: React.FC<GameViewProps> = ({ testID, state, dispatch, sendPicks }) => {
    const { edgeDeploy, fpUser } = useServices();
    const { currentEvent, reviewPicksState, setReviewState } = useEvent();
    const { allAgreementsAccepted, agreementsTag } = useAgreements();
    const [gameType, setGameType] = useState<GameType>(GameType.STANDARD);
    const [propNumber, setPropNumber] = useState<number>(0);
    const scrollViewRef = React.useRef<ScrollView>(null);
    const hideButtons =
        currentEvent?.eventStatus === BettingEventStatus.BETTING_CLOSED ||
        currentEvent?.eventStatus === BettingEventStatus.PENDING_SCORING;

    useEffect(() => {
        if (reviewPicksState) {
            scrollViewRef.current?.scrollToEnd({ animated: true });
        }
        //check for game type
        if (currentEvent?.horseRacingPrompts.length !== 0 && currentEvent?.horseRacingPrompts !== undefined) {
            setGameType(GameType.HORSE_RACING);
            countPropNumber(currentEvent, GameType.HORSE_RACING);
        } else if (currentEvent?.platformEvents !== undefined) {
            setGameType(GameType.STANDARD);
            countPropNumber(currentEvent, GameType.STANDARD);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewPicksState]);

    const styles = useDynamicStyles(generateStyles);
    if (!currentEvent) {
        return <></>;
    }
    const countPropNumber = (event: FreeplayEvent, gameType: GameType) => {
        if (gameType === GameType.STANDARD) {
            setPropNumber(currentEvent.platformEvents.length * 3);
        } else {
            setPropNumber(currentEvent.horseRacingPrompts.length);
        }
    };

    const gameEntry: [PlatformEvent, PlatformOffer][] = currentEvent.platformEvents.flatMap((entry) =>
        entry.platformOffers.map((item) => [entry, item]),
    ) as [PlatformEvent, PlatformOffer][];

    const horseGameEntry: [HorseRaceEvent, HorseRacingPrompt][] = currentEvent.horseRacingPrompts.map((prompt) => [
        prompt.horseRaceEvent,
        prompt,
    ]) as [HorseRaceEvent, HorseRacingPrompt][];
    const submitButtonText = reviewPicksState ? `${fpUser ? "" : "Login & "}SUBMIT PICKS` : "REVIEW PICKS";

    const toggleReviewState = async () => {
        if (!reviewPicksState) {
            mixpanel.track(MixPanelEventEnum.REVIEW_PICKS_SCREEN_VIEWED, {
                freeplay_event_id: currentEvent.id,
            });
        }
        setReviewState(!reviewPicksState);
    };
    // const clearPicks = async () => {
    //     dispatch({ type: "clearPicks" });
    //     setReviewState(false);
    // };

    const groupGames = (gameEntries: [PlatformEvent, PlatformOffer][]) => {
        const groupedGames: Map<number, [PlatformEvent, PlatformOffer][]> = new Map<
            number,
            [PlatformEvent, PlatformOffer][]
        >();

        for (const gameEntry of gameEntries) {
            let group = groupedGames.get(gameEntry[0].id);

            if (!group) {
                group = [];
            }

            group.push(gameEntry);
            groupedGames.set(gameEntry[0].id, group);
        }

        return groupedGames;
    };
    const groupHorseRaceGames = (horseGameEntries: [HorseRaceEvent, HorseRacingPrompt][]) => {
        const groupedGames: Map<number, [HorseRaceEvent, HorseRacingPrompt][]> = new Map<
            number,
            [HorseRaceEvent, HorseRacingPrompt][]
        >();

        for (const gameEntry of horseGameEntries) {
            let group = groupedGames.get(gameEntry[0].id);

            if (!group) {
                group = [];
            }

            group.push(gameEntry);
            groupedGames.set(gameEntry[0].id, group);
        }
        return groupedGames;
    };

    const title = currentEvent.name;

    const date = format(new Date(currentEvent.bettingEnds), "LLLL d, h:mm b");
    const optional = currentEvent.prizeDescription || `Pick ${propNumber} props and compete with others!`;

    const ContainerView = edgeDeploy ? View : ScrollView;
    const gameGroups: Map<number, [PlatformEvent, PlatformOffer][]> = groupGames(gameEntry);
    const horseGameGroup: Map<number, [HorseRaceEvent, HorseRacingPrompt][]> = groupHorseRaceGames(horseGameEntry);

    const getGroupedProps = () => {
        const elements: JSX.Element[] = [];
        let picksStartIndex = 0;
        if (gameType === GameType.STANDARD) {
            gameGroups.forEach((entry) => {
                elements.push(
                    <GameGroup
                        key={`prop-group-${entry[0][0].id}`}
                        gameEntry={entry}
                        groupName={entry[0][0].name}
                        groupDate={entry[0][0].startTime}
                        disabled={reviewPicksState || hideButtons}
                        dispatch={dispatch}
                        state={state}
                        gameType={gameType}
                        picksStartIndex={picksStartIndex}
                    />,
                );
                picksStartIndex += entry.length;
            });
        } else {
            horseGameGroup.forEach((entry) => {
                elements.push(
                    <GameGroup
                        key={`prop-group-${entry[0][0].id}`}
                        gameEntryWithHorseEvents={entry}
                        // horseProps={}
                        groupName={`${entry[0][0].trackDisplayName} - Race ${entry[0][0].raceId}`}
                        groupDate={parseISO(entry[0][0].correctedPostTime)}
                        disabled={reviewPicksState || hideButtons}
                        dispatch={dispatch}
                        state={state}
                        gameType={gameType}
                        picksStartIndex={picksStartIndex}
                    />,
                );
                picksStartIndex += entry.length;
            });
        }

        return elements;
    };
    const checkForUndefinedValues = (arr: SubmitPicks[] | SubmitHorseRacePicks[]): boolean => {
        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === undefined) return true;
        }
        return false;
    };

    return (
        <View style={edgeDeploy ? styles.edgeContainer : styles.container}>
            <GameTitle
                title={title}
                date={date}
                optionalText={optional}
                backgroundImage={currentEvent.game.league === FreeplayLeague.Other ? DefaultTSImage : DefaultImage}
                reviewPicksState={reviewPicksState}
            />
            {!reviewPicksState && (
                <>
                    {!hideButtons && <CountDownTimer inDate={currentEvent.bettingEnds} />}

                    {!hideButtons && (
                        <View style={[styles.buttonContainer, edgeDeploy ? styles.edgeButtonContainer : {}]}>
                            {gameType === GameType.HORSE_RACING ? null : (
                                <FavouritesButton state={state} dispatch={dispatch} />
                            )}
                            <RandomButton state={state} dispatch={dispatch} />
                        </View>
                    )}
                </>
            )}
            <ContainerView style={styles.selectionContainer}>
                <View style={[styles.choiceContainer, edgeDeploy ? styles.edgeChoiceContainer : {}]}>
                    {getGroupedProps()}
                </View>
            </ContainerView>
            {!hideButtons && (
                <View
                    style={[
                        styles.bottomButtonsContainer,
                        edgeDeploy && styles.edgeBottomButtonsContainer,
                        reviewPicksState && styles.hasTerms,
                    ]}
                >
                    {reviewPicksState && agreementsTag}
                    <RoundedButton
                        config={{
                            buttonText: submitButtonText,
                            size: ButtonSize.LARGE,
                            width: edgeDeploy ? 300 : 250,
                            height: edgeDeploy ? 60 : 50,
                            theme: ButtonTheme.ACCENTED,
                            disabled: reviewPicksState
                                ? !allAgreementsAccepted
                                : !(
                                      state.choices.length === gameEntry.length &&
                                      state.horseRaceChoices.length === horseGameEntry.length &&
                                      !checkForUndefinedValues(state.choices) &&
                                      !checkForUndefinedValues(state.horseRaceChoices)
                                  ),
                            margin: { left: reviewPicksState ? 10 : 0 },
                        }}
                        testID={`next-game-button`}
                        onPress={
                            reviewPicksState
                                ? sendPicks
                                : () => {
                                      toggleReviewState();
                                  }
                        }
                    />
                </View>
            )}
        </View>
    );
};

const generateStyles = () => {
    return EStyleSheet.create({
        [MediaQueries.MEDIUM]: {
            edgeContainer: {
                padding: 15,
                borderRadius: 0,
                marginVertical: 0,
            },
            edgeMiddleButton: {
                // marginHorizontal: "10px!important",
                marginHorizontal: "10px",
            },
            edgeChoiceContainer: {
                paddingHorizontal: 0,
            },
        },
        [MediaQueries.SMALL]: {
            edgeBottomButtonsContainer: {
                height: 90,
            },
        },
        middleButton: {
            // marginHorizontal: "10px!important",
            marginHorizontal: "10px",
        },
        edgeMiddleButton: {
            // marginHorizontal: "30px!important",
            marginHorizontal: "30px",
        },
        edgeContainer: {
            width: "100%",
            maxWidth: 860,
            marginHorizontal: "auto",
            padding: 30,
            marginVertical: 30,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            flex: 1,
            alignItems: "center",
        },
        container: {
            flex: 1,
            alignItems: "center",
        },

        buttonContainer: {
            width: "100%",
            flexDirection: "row",
            justifyContent: "center",
            paddingHorizontal: 10,
        },
        edgeButtonContainer: {
            paddingHorizontal: 0,
        },
        selectionContainer: {
            flex: 1,
            flexDirection: "column",
            alignSelf: "stretch",
        },
        bottomButtonsContainer: {
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowOpacity: 0.5,
            shadowRadius: 16,
            shadowColor: () => EStyleSheet.value(GlobalStyles.DEFAULT_SHADOW),
            width: "100%",
            height: 70,
            paddingTop: 10,
            paddingHorizontal: 10,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.GAME_CTA_BACKGROUND_COLOUR),
        },
        edgeBottomButtonsContainer: {
            height: 100,
            paddingHorizontal: 0,
        },
        hasTerms: {
            height: 180,
        },
        choiceContainer: {
            justifyContent: "center",
            paddingHorizontal: 10,
        },
        edgeChoiceContainer: {},
    });
};
